import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/config/api';
import { handleDemoRequest } from '@/demo/demoHandlers';
import { useDemoStore } from '@/stores/demoStore';
import type {
  ApiResponse,
  GamePlatform,
  LabDiscoverItem,
  LabLineage,
  LabQuizDraft,
  LabQuizExportFile,
  LabShowcaseState,
  LabQuizResponsePage,
  LabQuizView,
  LabProjectDetail,
  LabProjectSummary,
  LabRemixCreated,
  LabRemixOffer,
  LabSourceEngine,
  LabTeamDetail,
  LabTeamList,
  LabsContext,
  LabVisibility,
  ReleaseUpdate,
  User,
} from './types';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const LOGIN_ENDPOINTS = ['/auth/institutional-login', '/auth/verify-otp'];

client.interceptors.request.use((config) => {
  if (!useDemoStore.getState().isDemoMode) return config;
  config.adapter = (request) => demoResponse(request);
  return config;
});

function demoResponse(config: InternalAxiosRequestConfig) {
  const outcome = handleDemoRequest(config.url || '', demoBody(config.data));
  if (!outcome.ok) {
    return Promise.reject(new AxiosError(
      outcome.message,
      AxiosError.ERR_BAD_REQUEST,
      config,
      undefined,
      { data: { message: outcome.message }, status: outcome.status, statusText: 'Error', headers: {}, config },
    ));
  }
  return Promise.resolve({
    data: { success: true, data: outcome.data },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  });
}

function demoBody(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) return {};
  return data ?? {};
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = typeof error.config?.url === 'string' ? error.config.url : '';
    const isLoginEndpoint = LOGIN_ENDPOINTS.some((path) => url.includes(path));
    if (axios.isAxiosError(error) && error.response?.status === 401 && !isLoginEndpoint) {
      void import('@/stores/authStore').then(({ useAuthStore }) => {
        useAuthStore.setState({ user: null, isAuthenticated: false, isInitializing: false, isLoading: false });
        if (window.location.pathname.startsWith('/login')) return;
        const returnTo = `${window.location.pathname}${window.location.search}`;
        window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      });
    }
    return Promise.reject(error);
  },
);

async function post<T>(url: string, body?: unknown): Promise<T> {
  const response = await client.post<ApiResponse<T>>(url, body ?? {});
  return response.data.data;
}

export type InstitutionalLoginResult =
  | { action: 'redirect_sso'; redirectUrl: string; provider: string }
  | { action: 'otp_sent'; isNewUser: boolean };

export const authApi = {
  me: () => post<User>('/auth/me'),
  logout: () => client.post('/auth/logout'),
  startInstitutionalLogin: (email: string, target: string) =>
    post<InstitutionalLoginResult>('/auth/institutional-login', { email, portal: 'student', client: 'web', target }),
  verifyOtp: (email: string, token: string, firstName?: string, lastName?: string) =>
    post<{ user: User; needsOnboarding?: boolean; needsNameConfirmation?: boolean; needsOrgNoticeConsent?: boolean }>(
      '/auth/verify-otp',
      { email, token, ...(firstName ? { firstName } : {}), ...(lastName ? { lastName } : {}) },
    ),
  getOnboardingInfo: () =>
    post<{ firstName: string; lastName: string; orgName: string | null; notice: { id: string; title: string; url: string } | null }>('/auth/onboarding-info'),
  completeOnboarding: (input: { firstName: string; lastName: string; marketingConsent: boolean; privacyAccepted: boolean; orgNoticeId?: string | null }) =>
    post<{ user: User }>('/auth/complete-onboarding', input),
  acceptOrgNotice: (orgNoticeId: string) => post<{ user: User }>('/auth/accept-org-notice', { orgNoticeId }),
};

export const labsApi = {
  context: (orgSlug?: string) => post<LabsContext>('/labs/context', orgSlug ? { orgSlug } : {}),
  listProjects: (orgSlug: string) => post<LabProjectSummary[]>('/labs/projects/list', { orgSlug }),
  createProject: (
    orgSlug: string,
    input: { title: string; unitId: string | null; teamId?: string; teamName?: string },
  ) => post<LabProjectDetail>('/labs/projects/create', { orgSlug, ...input }),
  getProject: (projectId: string) => post<LabProjectDetail>('/labs/projects/get', { projectId }),
  updateProjectUnit: (projectId: string, unitId: string | null) =>
    post<LabProjectDetail>('/labs/projects/update-unit', { projectId, unitId }),
  updateProjectDrm: (projectId: string, isDrmFree: boolean) =>
    post<LabProjectDetail>('/labs/projects/update-drm', { projectId, isDrmFree }),
  deleteProject: (projectId: string) => post<{ success: boolean }>('/labs/projects/delete', { projectId }),
  listTeams: (orgSlug: string, page = 1, pageSize = 24) =>
    post<LabTeamList>('/labs/teams/list', { orgSlug, page, pageSize }),
  createTeam: (orgSlug: string, name: string, description: string | null) =>
    post<LabTeamDetail>('/labs/teams/create', { orgSlug, name, description }),
  getTeam: (teamId: string) => post<LabTeamDetail>('/labs/teams/get', { teamId }),
  updateTeam: (teamId: string, input: { name?: string; description?: string | null }) =>
    post<LabTeamDetail>('/labs/teams/update', { teamId, ...input }),
  addTeamMember: (teamId: string, email: string, role: 'manager' | 'member') =>
    post<LabTeamDetail>('/labs/teams/members/add', { teamId, email, role }),
  removeTeamMember: (teamId: string, userId: string) =>
    post<LabTeamDetail | null>('/labs/teams/members/remove', { teamId, userId }),
  setTeamRole: (teamId: string, userId: string, role: 'manager' | 'member') =>
    post<LabTeamDetail>('/labs/teams/members/set-role', { teamId, userId, role }),
  transferTeamOwnership: (teamId: string, userId: string) =>
    post<LabTeamDetail>('/labs/teams/ownership/transfer', { teamId, userId }),
  startRelease: (projectId: string) => post<LabProjectDetail>('/labs/projects/start-release', { projectId }),
  updateRelease: (releaseId: string, changes: ReleaseUpdate) =>
    post<LabProjectDetail>('/labs/releases/update', { releaseId, ...changes }),
  submitRelease: (releaseId: string) => post<LabProjectDetail>('/labs/releases/submit', { releaseId }),
  approveRelease: (releaseId: string) => post<unknown>('/labs/review/decide', { releaseId, action: 'approve' }),
  cancelSubmission: (releaseId: string) => post<LabProjectDetail>('/labs/releases/cancel-submission', { releaseId }),
  narrowVisibility: (releaseId: string, visibility: LabVisibility) =>
    post<LabProjectDetail>('/labs/releases/narrow-visibility', { releaseId, visibility }),
  stopRemixing: (releaseId: string) => post<LabProjectDetail>('/labs/releases/stop-remixing', { releaseId }),
  uploadMedia: async (releaseId: string, type: 'icon' | 'banner', file: File) => {
    const form = new FormData();
    form.append('releaseId', releaseId);
    form.append('type', type);
    form.append('file', file);
    const response = await client.post<ApiResponse<LabProjectDetail>>('/labs/releases/media', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },
  initiateBuild: (input: { releaseId: string; platform: GamePlatform; filename: string; fileSize: number; totalParts: number; checksum: string }) =>
    post<{ buildId: string; uploadId: string; key: string }>('/labs/builds/initiate', input),
  getBuildPartUrls: (buildId: string, partNumbers: number[]) =>
    post<{ urls: Array<{ partNumber: number; url: string }> }>('/labs/builds/part-urls', { buildId, partNumbers }),
  completeBuild: (buildId: string, releaseId: string, parts: Array<{ partNumber: number; etag: string }>) =>
    post<LabProjectDetail>('/labs/builds/complete', { buildId, releaseId, parts }),
  abortBuild: (buildId: string) => post<LabProjectDetail>('/labs/builds/abort', { buildId }),
  selectExecutable: (buildId: string, executablePath: string) =>
    post<LabProjectDetail>('/labs/builds/select-executable', { buildId, executablePath }),
  attachBuild: (releaseId: string, buildId: string) => post<LabProjectDetail>('/labs/builds/attach', { releaseId, buildId }),
  removeBuild: (releaseId: string, platform: GamePlatform) =>
    post<LabProjectDetail>('/labs/builds/remove', { releaseId, platform }),
  initiateSource: (input: {
    releaseId: string;
    filename: string;
    fileSize: number;
    totalParts: number;
    checksum: string;
    manifest: {
      formatVersion: 1;
      engine: LabSourceEngine;
      engineVersion: string | null;
      projectRoot: string;
      fileCount: number;
      unpackedSize: number;
      exclusions: string[];
    };
  }) => post<{ sourcePackageId: string; uploadId: string; key: string }>('/labs/sources/initiate', input),
  getSourcePartUrls: (sourcePackageId: string, partNumbers: number[]) =>
    post<{ urls: Array<{ partNumber: number; url: string }> }>(
      '/labs/sources/part-urls',
      { sourcePackageId, partNumbers },
    ),
  completeSource: (
    sourcePackageId: string,
    releaseId: string,
    parts: Array<{ partNumber: number; etag: string }>,
  ) => post<LabProjectDetail>('/labs/sources/complete', { sourcePackageId, releaseId, parts }),
  abortSource: (sourcePackageId: string) =>
    post<LabProjectDetail>('/labs/sources/abort', { sourcePackageId }),
  attachSource: (releaseId: string, sourcePackageId: string) =>
    post<LabProjectDetail>('/labs/sources/attach', { releaseId, sourcePackageId }),
  removeSource: (releaseId: string) =>
    post<LabProjectDetail>('/labs/sources/remove', { releaseId }),
  discover: (orgSlug: string) => post<LabDiscoverItem[]>('/labs/discover', { orgSlug }),
  remixOffer: (releaseId: string) => post<LabRemixOffer>('/labs/remixes/offer', { releaseId }),
  remixShowcaseOffer: (slug: string) => post<LabRemixOffer>('/labs/remixes/showcase-offer', { slug }),
  createRemix: (
    orgSlug: string,
    input: { parentReleaseId: string; termsVersion: string; title: string; unitId: string | null; teamId?: string; teamName?: string },
  ) => post<LabRemixCreated>('/labs/remixes/create', { orgSlug, ...input }),
  createShowcaseRemix: (
    orgSlug: string,
    input: { slug: string; termsVersion: string; title: string; unitId: string | null; teamId?: string; teamName?: string },
  ) => post<LabRemixCreated>('/labs/remixes/showcase-create', { orgSlug, ...input }),
  lineage: (projectId: string, page = 1) => post<LabLineage>('/labs/remixes/lineage', { projectId, page, pageSize: 20 }),
  downloadSource: (releaseId: string) => post<{ url: string; expiresIn: number }>('/labs/remixes/source', { releaseId }),
  listQuizzes: (projectId: string) => post<LabQuizView[]>('/labs/quizzes/list', { projectId }),
  createQuiz: (projectId: string, title: string, draft: LabQuizDraft) =>
    post<LabQuizView>('/labs/quizzes/create', { projectId, title, draft }),
  updateQuiz: (quizId: string, input: { title?: string; draft?: LabQuizDraft }) =>
    post<LabQuizView>('/labs/quizzes/update', { quizId, ...input }),
  openQuiz: (quizId: string) => post<LabQuizView>('/labs/quizzes/open', { quizId }),
  closeQuiz: (quizId: string) => post<LabQuizView>('/labs/quizzes/close', { quizId }),
  deleteQuiz: (quizId: string) => post<{ success: boolean }>('/labs/quizzes/delete', { quizId }),
  quizResponses: (quizId: string, page = 1, versionId?: string) =>
    post<LabQuizResponsePage>('/labs/quizzes/responses', { quizId, page, pageSize: 20, ...(versionId ? { versionId } : {}) }),
  exportQuiz: (quizId: string, format: 'csv' | 'json', versionId?: string) =>
    post<LabQuizExportFile>('/labs/quizzes/export', { quizId, format, ...(versionId ? { versionId } : {}) }),
  showcase: (projectId: string) => post<LabShowcaseState>('/labs/showcase/get', { projectId }),
  saveShowcase: (
    releaseId: string,
    input: { openSourceLicense: string; assetLicense: string; assetDeclaration: string | null; sourcePublished: boolean },
  ) => post<LabShowcaseState>('/labs/showcase/save', { releaseId, ...input }),
  submitShowcase: (
    releaseId: string,
    input: { openSourceLicense: string; assetLicense: string; assetDeclaration: string | null; sourcePublished: boolean },
  ) => post<LabShowcaseState>('/labs/showcase/submit', { releaseId, ...input }),
  withdrawShowcase: (releaseId: string) => post<LabShowcaseState>('/labs/showcase/withdraw', { releaseId }),
};

export function getErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; details?: Array<{ message: string }> } | undefined;
    return data?.details?.[0]?.message ?? data?.message ?? fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}
